import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface ConfirmacaoState {
  name: string;
  email: string;
  phone: string;
  company: string;
  eventType: string;
  eventSubtype: string;
  participants: string;
  channels: string;
  city: string;
  venue: string;
  startDate: string;
  endDate: string;
  flexibility: string;
  duration: string;
  message: string;
}

function formatDateBR(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  corporativo: 'Evento Corporativo',
  congresso: 'Congresso / Conferência',
  feira: 'Feira e Exposição',
  summit: 'Summit / Fórum',
  multilingue: 'Evento Multilíngue',
  outro: 'Outro Tipo',
};

const FLEXIBILITY_LABELS: Record<string, string> = {
  fixed: 'Data fixada',
  week: 'Flexível ±1 semana',
  month: 'Flexível ±1 mês',
  tbd: 'A definir',
};

export default function OrcamentoConfirmacaoPage() {
  const location = useLocation();
  const state = location.state as ConfirmacaoState | null;

  useEffect(() => {
    document.title = 'Orçamento Solicitado com Sucesso | STIB';
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // This is a post-submit confirmation page — should not be indexed
    let robotsMeta = document.querySelector('meta[name="robots"][data-confirmacao]') as HTMLMetaElement | null;
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      robotsMeta.setAttribute('data-confirmacao', '1');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'noindex, nofollow';

    return () => {
      const el = document.querySelector('meta[name="robots"][data-confirmacao]');
      if (el) el.remove();
    };
  }, []);

  const dateDisplay = () => {
    if (!state) return null;
    if (state.flexibility === 'tbd') return 'A definir';
    if (state.startDate && state.endDate) {
      if (state.startDate === state.endDate) return formatDateBR(state.startDate);
      return `${formatDateBR(state.startDate)} → ${formatDateBR(state.endDate)}`;
    }
    return null;
  };

  const whatsappText = state
    ? encodeURIComponent(
        `Olá! Acabei de solicitar um orçamento pelo site.\n\n` +
        `*Nome:* ${state.name}\n` +
        `*Evento:* ${state.eventSubtype || EVENT_TYPE_LABELS[state.eventType] || state.eventType}\n` +
        `*Cidade:* ${state.city}${state.venue ? ` — ${state.venue}` : ''}\n` +
        `*Participantes:* ${state.participants}\n` +
        `*Data:* ${dateDisplay() || 'A definir'}\n\n` +
        `Gostaria de confirmar o recebimento e agilizar a proposta!`
      )
    : encodeURIComponent('Olá! Acabei de enviar um pedido de orçamento pelo site e gostaria de confirmar o recebimento.');

  const summaryItems = state
    ? [
        {
          icon: 'ri-user-line',
          label: 'Nome',
          value: state.name,
          highlight: false,
        },
        {
          icon: 'ri-mail-line',
          label: 'E-mail',
          value: state.email,
          highlight: false,
        },
        {
          icon: 'ri-phone-line',
          label: 'Telefone',
          value: state.phone,
          highlight: false,
        },
        state.company
          ? { icon: 'ri-building-line', label: 'Empresa', value: state.company, highlight: false }
          : null,
        {
          icon: 'ri-calendar-event-line',
          label: 'Tipo de evento',
          value: `${EVENT_TYPE_LABELS[state.eventType] || state.eventType}${state.eventSubtype ? ` — ${state.eventSubtype}` : ''}`,
          highlight: true,
        },
        {
          icon: 'ri-group-line',
          label: 'Participantes',
          value: state.participants,
          highlight: true,
        },
        {
          icon: 'ri-broadcast-line',
          label: 'Canais simultâneos',
          value: state.channels,
          highlight: true,
        },
        {
          icon: 'ri-map-pin-line',
          label: 'Cidade / Local',
          value: `${state.city}${state.venue ? ` — ${state.venue}` : ''}`,
          highlight: true,
        },
        dateDisplay()
          ? { icon: 'ri-calendar-2-line', label: 'Data', value: dateDisplay()!, highlight: true }
          : null,
        state.flexibility && state.flexibility !== 'fixed'
          ? {
              icon: 'ri-time-line',
              label: 'Flexibilidade',
              value: FLEXIBILITY_LABELS[state.flexibility] || state.flexibility,
              highlight: false,
            }
          : null,
        state.duration
          ? { icon: 'ri-timer-line', label: 'Duração por dia', value: state.duration, highlight: false }
          : null,
        state.message
          ? { icon: 'ri-chat-3-line', label: 'Observações', value: state.message, highlight: false }
          : null,
      ].filter(Boolean)
    : [];

  return (
    <>
      <Header />

      <main className="min-h-screen bg-gradient-to-b from-secondary/5 to-white pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-6">

          {/* Success hero */}
          <div className="text-center mb-14">
            <div
              className="relative w-28 h-28 flex items-center justify-center mx-auto mb-8"
              style={{ animation: 'popIn 0.5s cubic-bezier(0.22,0.68,0,1.4) both' }}
            >
              <div className="absolute inset-0 bg-green-100 rounded-full"></div>
              <div
                className="absolute inset-0 bg-green-200/50 rounded-full"
                style={{ animation: 'ping 1.8s ease-out 0.4s infinite' }}
              ></div>
              <i className="ri-check-double-line text-green-500 text-5xl relative z-10"></i>
            </div>

            <h1
              className="font-heading font-extrabold text-4xl md:text-5xl text-secondary mb-4 leading-tight"
              style={{ animation: 'fadeSlideUp 0.5s 0.15s both' }}
            >
              Orçamento solicitado<br />com sucesso!
            </h1>
            <p
              className="font-body text-gray-500 text-lg max-w-xl mx-auto leading-relaxed"
              style={{ animation: 'fadeSlideUp 0.5s 0.25s both' }}
            >
              Nossa equipe recebeu sua solicitação e retornará com uma{' '}
              <strong className="text-secondary">proposta personalizada em até 24 horas</strong>.
              {state?.email && (
                <> Você também receberá uma cópia no e-mail <strong className="text-secondary">{state.email}</strong>.</>
              )}
            </p>

            {/* Timeline badge */}
            <div
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-primary/10 rounded-full"
              style={{ animation: 'fadeSlideUp 0.5s 0.35s both' }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-timer-flash-line text-primary text-base"></i>
              </div>
              <span className="font-body text-sm font-semibold text-primary">
                Protocolo: #{Math.floor(Date.now() / 1000).toString(36).toUpperCase()}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Summary card */}
            <div
              className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 overflow-hidden"
              style={{ boxShadow: '0 4px 32px rgba(56,71,116,0.07)', animation: 'fadeSlideUp 0.5s 0.3s both' }}
            >
              <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-heading font-bold text-xl text-secondary">
                  Resumo da solicitação
                </h2>
                <span className="font-body text-xs text-gray-400 font-medium">
                  {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                </span>
              </div>

              {state ? (
                <div className="p-8">
                  {/* Contact section */}
                  <div className="mb-6">
                    <p className="font-body text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
                      Dados de Contato
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {summaryItems
                        .filter((i) => i && !i.highlight)
                        .map((item) => (
                          <div key={item!.label} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                            <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg flex-shrink-0 border border-gray-100">
                              <i className={`${item!.icon} text-gray-400 text-sm`}></i>
                            </div>
                            <div className="min-w-0">
                              <p className="font-body text-xs text-gray-400">{item!.label}</p>
                              <p className="font-body text-sm font-semibold text-secondary truncate">{item!.value}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Event details */}
                  <div>
                    <p className="font-body text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
                      Detalhes do Evento
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {summaryItems
                        .filter((i) => i && i.highlight)
                        .map((item) => (
                          <div
                            key={item!.label}
                            className="flex items-start gap-3 p-4 rounded-xl border border-secondary/10 bg-secondary/3"
                          >
                            <div className="w-9 h-9 flex items-center justify-center bg-primary/10 rounded-xl flex-shrink-0">
                              <i className={`${item!.icon} text-primary text-sm`}></i>
                            </div>
                            <div className="min-w-0">
                              <p className="font-body text-xs text-gray-400">{item!.label}</p>
                              <p className="font-body text-sm font-bold text-secondary">{item!.value}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {state.message && (
                    <div className="mt-5 p-4 rounded-xl bg-gray-50 border border-gray-100">
                      <p className="font-body text-xs font-bold text-gray-400 mb-1">Observações</p>
                      <p className="font-body text-sm text-gray-600 leading-relaxed">{state.message}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-8 text-center py-16">
                  <i className="ri-file-list-3-line text-gray-200 text-5xl mb-4 block"></i>
                  <p className="font-body text-sm text-gray-400">
                    Resumo não disponível. Acesse esta página após enviar o formulário.
                  </p>
                  <Link
                    to="/orcamento"
                    className="inline-flex items-center gap-2 mt-6 text-primary font-body text-sm font-semibold hover:underline cursor-pointer"
                  >
                    Solicitar orçamento
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}
            </div>

            {/* Right sidebar */}
            <div className="space-y-5" style={{ animation: 'fadeSlideUp 0.5s 0.45s both' }}>

              {/* Next steps */}
              <div className="bg-secondary rounded-3xl p-7 text-white">
                <p className="font-body text-xs font-bold uppercase tracking-widest text-primary mb-5">
                  O que acontece agora?
                </p>
                <div className="space-y-5">
                  {[
                    {
                      step: '01',
                      title: 'Análise da solicitação',
                      desc: 'Nossa equipe revisa os detalhes do seu evento agora.',
                    },
                    {
                      step: '02',
                      title: 'Proposta personalizada',
                      desc: 'Você recebe uma proposta detalhada em até 24 horas.',
                    },
                    {
                      step: '03',
                      title: 'Confirmação e contrato',
                      desc: 'Aprovando, enviamos NF-e e contrato para formalizar.',
                    },
                    {
                      step: '04',
                      title: 'Suporte até o dia do evento',
                      desc: 'Técnico STIB presente e equipamentos certificados.',
                    },
                  ].map((s) => (
                    <div key={s.step} className="flex gap-4">
                      <div className="w-8 h-8 flex items-center justify-center bg-primary rounded-full flex-shrink-0 text-white font-heading font-bold text-xs">
                        {s.step}
                      </div>
                      <div>
                        <p className="font-body text-sm font-bold text-white">{s.title}</p>
                        <p className="font-body text-xs text-white/65 leading-relaxed mt-0.5">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* WhatsApp CTA */}
              <a
                href={`https://wa.me/5511917776227?text=${whatsappText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-5 bg-green-500 rounded-2xl hover:bg-green-600 transition-colors cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                  <i className="ri-whatsapp-line text-white text-2xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body font-bold text-white text-sm">Falar com a equipe agora</p>
                  <p className="font-body text-xs text-white/80">Confirmação imediata via WhatsApp</p>
                </div>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-line text-white group-hover:translate-x-1 transition-transform"></i>
                </div>
              </a>

              {/* Garantias */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <p className="font-body text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
                  Garantias STIB
                </p>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-shield-check-line', text: 'Orçamento 100% gratuito e sem compromisso' },
                    { icon: 'ri-time-line', text: 'Resposta em até 24 horas' },
                    { icon: 'ri-file-text-line', text: 'NF-e e contrato formal' },
                    { icon: 'ri-customer-service-2-line', text: 'Técnico presente no evento' },
                  ].map((g) => (
                    <div key={g.text} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
                        <i className={`${g.icon} text-green-500 text-sm`}></i>
                      </div>
                      <p className="font-body text-xs text-gray-600">{g.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Back links */}
              <div className="flex flex-col gap-2">
                <Link
                  to="/"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-gray-200 font-body text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-home-line text-sm"></i>
                  </div>
                  Voltar ao início
                </Link>
                <Link
                  to="/orcamento"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-primary/30 font-body text-sm text-primary hover:bg-primary/5 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-refresh-line text-sm"></i>
                  </div>
                  Novo orçamento
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes popIn {
          0%   { transform: scale(0.5); opacity: 0; }
          70%  { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes ping {
          0%   { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(1.6); opacity: 0; }
        }
      `}</style>
    </>
  );
}